import React, { useEffect, useState, useRef } from "react";
import Menu from "@mui/material/Menu";
import { Table, TableProps, Form, Input, Popconfirm, Space } from "antd";
import Button from "@mui/material/Button";
import {
  Typography as Typographys,
  Tag,
  Button as Buttons,
  Select,
  notification,
} from "antd";
import type { InputRef, TableColumnType } from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import EventController from "./Controller/event_controller.ts";
import DocumentController from "./Controller/document_controller.ts";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import Stack from "@mui/material/Stack";
import {
  DeleteOutlined,
  UploadOutlined,
  EditOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import CustomSeparator from "../../layouts/AdminLayout/Header/Breadcrumbs.js";
import { Typography } from "@mui/material";
import Highlighter from "react-highlight-words";
import { createStyles } from "antd-style";
import { DataObject } from "@mui/icons-material";

const useStyle = createStyles(({ css }) => {
  return {
    customTable: css`
      .ant-table {
        .ant-table-container {
          .ant-table-body,
          .ant-table-content {
            scrollbar-width: thin;
            scrollbar-color: #eaeaea transparent;
            scrollbar-gutter: stable;
          }
        }
      }
    `,
  };
});

const { Paragraph } = Typographys;
const { TextArea } = Input;
const userID = localStorage.getItem("userID");

type NotificationType = "success" | "info" | "warning" | "error";

type TableRowSelection<T extends object = object> =
  TableProps<T>["rowSelection"];

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface DataType {
  userID: string;
  document_categoryID: string;
  label: string;
}

type DataIndex = keyof DataType;

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType: "select" | "text";
  record: DataType;
  index: number;
  options: any;
}

const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  options,
  ...restProps
}) => {
  let inputNode;

  inputNode = <TextArea autoSize={{ minRows: 2, maxRows: 10 }} />;

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            {
              required: true,
              message: `Please Input ${title}!`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

const CheckboxList = ({ baseColumns, onChange }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // State to keep track of selected checkboxes
  const [checked, setChecked] = useState(
    baseColumns.map((col) => col.dataIndex) // Initially select all columns
  );

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggle = (value: Number) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
    onChange(newChecked); // Call the parent onChange handler
  };

  return (
    <>
      <Button
        id="demo-positioned-button"
        aria-controls={open ? "demo-positioned-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        variant="contained"
        style={{ backgroundColor: "#d33333" }}
      >
        Columns
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            // maxHeight: 400,
            width: "20ch",
          },
        }}
      >
        <List
          sx={{ width: "100%", maxWidth: 360, bgcolor: "background.paper" }}
        >
          {baseColumns.map((value) => {
            const labelId = `checkbox-list-label-${value.dataIndex}`;
            return (
              <ListItem key={value.dataIndex} disablePadding>
                <ListItemButton
                  role={undefined}
                  onClick={handleToggle(value.dataIndex)}
                  dense
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={checked.includes(value.dataIndex)}
                      tabIndex={-1}
                      disableRipple
                      inputProps={{ "aria-labelledby": labelId }}
                    />
                  </ListItemIcon>
                  <ListItemText id={labelId} primary={value.title} />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Menu>
    </>
  );
};

function App() {
  const [api, contextHolder] = notification.useNotification();

  const openNotificationWithIcon = (
    type: NotificationType,
    message: String
  ) => {
    api[type]({
      message: "Success",
      description: message,
    });
  };

  const [rows, setRows] = useState<any[]>([]);
  const [data, setData] = useState<DataType[]>(rows);

  useEffect(() => {
    DocumentController.getResourcesCategories().then((response) => {
      if (Array.isArray(response)) {
        const dynamicRows = response.map((item, index) => ({
          key: index,
          document_categoryID: item.document_categoryID,
          label: item.label,
        }));
        setRows(Array.from(dynamicRows));
      } else {
      }
    });

    setData(rows);
  }, []);

  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [editingKey, setEditingKey] = useState("");

  const isEditing = (record: DataType) => {
    return record.document_categoryID === editingKey;
  };
  const edit = (
    record: Partial<DataType> & { document_categoryID: string }
  ) => {
    form.setFieldsValue({ name: "", age: "", address: "", ...record });
    setEditingKey(record.document_categoryID);
  };

  const cancel = () => {
    setEditingKey("");
  };

  const delete_record = (rowId: any, userID) => {
    const payload = new FormData();
    payload.append("ID", rowId);
    payload.append("userID", userID);

    console.log(rowId);
    DocumentController.deleteDocumentCategoryRow(payload).then((response) => {
      if (response?.status === "success") {
        openNotificationWithIcon(
          "success",
          "Your data has been deleted successfully!"
        );
        const dynamicRows = response.data.map((item: any, index: number) => ({
          key: index,
          document_categoryID: item.document_categoryID,
          label: item.label,
        }));

        setRows(dynamicRows);
      }
    });
  };
  const save = async (key: any, userID) => {
    try {
      const row = (await form.validateFields()) as DataType;

      // Use the key directly as the document_categoryID
      const payload = {
        ID: key, // Pass the key directly as the document_categoryID
        data: row, // Include the updated data from the form
        userID: userID,
      };

      console.log("Payload to save:", payload); // Debugging step

      DocumentController.saveRowDocumentCategory(payload).then((response) => {
        if (response?.status === "success") {
          openNotificationWithIcon(
            "success",
            "Your data has been saved successfully!"
          );

          // Update rows with the response data
          const dynamicRows = response.data.map((item: any, index: number) => ({
            key: index,
            document_categoryID: item.document_categoryID,
            label: item.label,
          }));

          setRows(dynamicRows); // Update the table rows
        }
      });

      setEditingKey(""); // Clear the editing key
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };
  // **************************** SEARCH FILTER ANTD TABLE *****************************************************
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef<InputRef>(null);

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: DataIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): TableColumnType<DataType> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Buttons
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Buttons>
          <Buttons
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Buttons>
          <Buttons
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false });
              setSearchText((selectedKeys as string[])[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Buttons>
          <Buttons
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            close
          </Buttons>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (open) => {
      if (open) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });
  // ************************************************************************************************************

  const baseColumns = [
    /* Resources Category Name */
    {
      title: "Resources Category Name",
      dataIndex: "label",
      key: "label",
      editable: true,
      ...getColumnSearchProps("label"),
    },
    /* Operation */
    {
      title: "Operation",
      dataIndex: "operation",
      editable: false,
      width: 150,
      render: (_: any, record: DataType) => {
        const editable = isEditing(record);
        return editable ? (
          <span>
            <Space>
              <Buttons
                onClick={() => save(record.document_categoryID, userID)}
                color="default"
                variant="solid"
              >
                Save
              </Buttons>
              <Buttons color="default" variant="outlined">
                <Popconfirm title="Sure to cancel?" onConfirm={cancel}>
                  Cancel
                </Popconfirm>
              </Buttons>
            </Space>
          </span>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              width: "90%",
              gap: "10px",
            }}
          >
            <Buttons
              color="default"
              variant="solid"
              disabled={editingKey !== ""}
              onClick={() => edit(record)}
              icon={<EditOutlined />}
            >
              Edit
            </Buttons>
            <Buttons
              type="primary"
              danger
              icon={<DeleteOutlined />}
              disabled={editingKey !== ""}
              // onClick={() => edit(record)}
            >
              <Popconfirm
                title="Sure to delete?"
                onConfirm={() =>
                  delete_record(record.document_categoryID, userID)
                }
              >
                Delete
              </Popconfirm>
            </Buttons>
          </div>
        );
      },
    },
  ];

  const onSelectChange = (
    newSelectedRowKeys: React.Key[],
    selectedRows: DataType[]
  ) => {
    console.log("selectedRowKeys changed: ", newSelectedRowKeys);
    setSelectedRowKeys(newSelectedRowKeys);

    // call api here
  };

  const rowSelection: TableRowSelection<DataType> = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const [checkedColumns, setCheckedColumns] = useState([
    "document_categoryID",
    "label",
    "operation",
  ]);

  const onChange = (checkedValues) => {
    setCheckedColumns(checkedValues);
  };

  // Create mergedColumns directly using baseColumns
  const mergedColumns = baseColumns
    .filter(
      (col) =>
        typeof col.dataIndex === "string" &&
        checkedColumns.includes(col.dataIndex)
    )
    .map((col) => {
      // Default to non-editable if `editable` is undefined
      if (!col.editable) {
        return col;
      }

      // Handle editable columns
      return {
        ...col,
        onCell: (record: DataType) => {
          const columnTitle =
            typeof col.title === "string" ? col.title : String(col.title);
          const columnDataIndex =
            typeof col.dataIndex === "string"
              ? col.dataIndex
              : String(col.dataIndex);
          return {
            record,
            dataIndex: columnDataIndex,
            title: columnTitle,
            editing: isEditing(record),
            // Include options if needed for specific columns
            // options:
            //   col.dataIndex === "event_category_type"
            //     ? categoryOptions
            //     : undefined,
          };
        },
      };
    });

  const { styles } = useStyle();

  return (
    <>
      <Box
        sx={{
          display: "flex",
          backgroundColor: "#e8e8e8",
          height: "100%", // Full viewport height
          minHeight: "150vh" /* scroll y for page */,
          width: "100vw", // Full viewport width
          overflow: "hidden", // Ensure no overflow from the Box itself
        }}
      >
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            display: "flex", // Ensures the table fills the remaining space
            flexDirection: "column",
            height: "100%", // Fill remaining height
            width: "100%", // Fill remaining width
          }}
        >
          <DrawerHeader />
          <CustomSeparator pageName="resources_category" />
          {contextHolder}

          <Typography
            gutterBottom
            variant="h4"
            sx={{ color: "text.secondary" }}
          >
            Resources Categories Directory
          </Typography>

          <Stack spacing={2} direction="row" sx={{ mb: 3 }}>
            <CheckboxList baseColumns={baseColumns} onChange={onChange} />
          </Stack>

          {/* Box to hold the table with full width/height */}
          <Box
            sx={{
              flexGrow: 1, // Ensures the table box expands
              backgroundColor: "#fff", // Set the background color for the table container
              height: "100%", // Ensures the table fills available height
              width: "100%", // Ensures the table fills available width
              overflow: "auto", // Allow scrolling if needed
              borderRadius: "8px", // Optional: for rounded corners
            }}
          >
            <Form form={form} component={false}>
              <Table<DataType>
                className={styles.customTable}
                components={{
                  body: { cell: EditableCell },
                }}
                bordered
                columns={mergedColumns}
                dataSource={rows}
                rowSelection={rowSelection}
                rowKey="document_categoryID" // Set your custom key here
                scroll={{ y: 200 * 5, x: "max-content" }} // Enables vertical and horizontal scrolling
              />
            </Form>
          </Box>
        </Box>
      </Box>
    </>
  );
}

export default App;
